import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ReferenceTable } from '../models/reference-table';
import { User, UserSearchRow } from '../models/user';
import { UserAffiliation } from '../models/user-affiliation';
import { UserAffiliationType } from '../models/user-affiliation-type';
import { UserEmployer } from '../models/user-employer';
import { UserIndividual } from '../models/user-individual';
import { UserSearchForm } from '../models/user-search-form';
import { formatPhoneNumbers, unformatPhoneNumbers } from '../utils/phone-number-util';

const getUserNameUrl = '/users/fetchUserName.html';
const searchUsersForIndividualUrl = '/users/searchUsersforIndividual.html';
const checkUsernameUrl = '/users/checkUsernameURL.html';
const getUserUrl = '/users/getUser.html';
const getUserByUserNameUrl = '/users/getUserByUserName.html';
const updateUserProfileUrl = '/users/updateUserProfile.html';
const updateMyUserProfileUrl = '/users/updateMyUserProfile.html';
const getAffiliationUrl = '/users/getAffiliation.html';
const updateAffiliationUrl = '/users/updateAffiliation.html';
const getUserAffiliationTypesUrl = '/users/getAffiliationTypes.html';
const getJobTitlesUrl = '/users/getJobTitles.html';
const getMessageTagsUrl = '/users/getMessageTags.html';
const getMessageSubjectsUrl = '/users/getMessageSubjects.html';
const getUserEmployersUrl = '/users/getUserEmployers.html';
const getRaceTitlesUrl = '/users/getRaceTitles.html';
const updateUserASMStatusUrl = '/users/updateUserASMStatus.html';
const updateUserSudSmiProgramDashboardStatusUrl = '/users/updateUserSudSmiProgramDashboardAccessStatus.html';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userCache: Map<number, User> = new Map();

  private _authenticatedUserUpdate: Subject<User> = new Subject();
  authenticatedUserUpdate: Observable<User> = this._authenticatedUserUpdate.asObservable();

  private _userASMStatusUpdate: Subject<void> = new Subject();
  userASMStatusUpdate: Observable<void> = this._userASMStatusUpdate.asObservable();

  private _userSudSmiAccessUpdate: Subject<void> = new Subject();
  userSudSmiAccessUpdate: Observable<void> = this._userSudSmiAccessUpdate.asObservable();

  constructor(private http: HttpClient) {}

  getUserName(userId: number | string): Observable<string> {
    return this.http.post(getUserNameUrl, '', {
      responseType: 'text',
      params: {
        userid: userId
      },
      headers: {
        Accept: 'text/plain, */*'
      }
    });
  }

  usernameExists(username: string): Observable<boolean> {
    return this.http.get<boolean>(checkUsernameUrl, {
      params: {
        username: username || ''
      }
    });
  }
  /**
   * Fetches a user.
   *
   * @param userId `userId` of user
   * @param useCache Whether or not to try fetching the user from `UserService`'s
   *                 in-memory cache. The request will continue normally if the cache does
   *                 not contain the specified `userId`.
   *
   *                 Defaults to `true`.
   */
  getUser(userId: number, useCache = true): Observable<User> {
    if (this.userCache.has(userId) && useCache) {
      return of(this.userCache.get(userId));
    }

    return this.http
      .get<User>(getUserUrl, {
        params: {
          userId: userId
        }
      })
      .pipe(
        tap((user) => {
          user.password = '';
          user.confirmPassword = '';
          user.organization = user.organization || null;
          formatPhoneNumbers(user);

          this.userCache.set(userId, user);
        })
      );
  }

  getUserByUserName(userName: string): Observable<User> {
    return this.http.get<User>(getUserByUserNameUrl, {
      params: {
        userName: userName
      }
    });
  }

  uncacheUser(userId: number) {
    this.userCache.delete(userId);
  }

  uncacheLinkedUsers(userId: number) {
    this.userCache.forEach((user) => {
      if (user.userId === userId || user.linkedUserId === userId) {
        this.uncacheUser(user.userId);
      }
    });
  }

  userExists(userId: number): Observable<boolean> {
    return this.getUser(userId).pipe(map((user) => user != null));
  }

  searchUsersForIndividual(userSearchForm: UserSearchForm): Observable<User[]> {
    return this.http.post<User[]>(searchUsersForIndividualUrl, UserIndividual.getInstance(userSearchForm));
  }

  updateAuthenticatedUser(user: User): Observable<User> {
    const clonedUser = cloneDeep(user);
    unformatPhoneNumbers(clonedUser);

    return this.http.post<User>(updateMyUserProfileUrl, clonedUser.mobileNo).pipe(
      tap((response) => {
        if (response) {
          this.uncacheLinkedUsers(user.userId);
          this._authenticatedUserUpdate.next(user);
        }
      })
    );
  }

  updateUser(user: User): Observable<User> {
    const clonedUser = cloneDeep(user);
    unformatPhoneNumbers(clonedUser);

    return this.http.post<User>(updateUserProfileUrl, clonedUser).pipe(
      tap((response) => {
        if (response) {
          this.uncacheLinkedUsers(user.userId);
          this._authenticatedUserUpdate.next(user);
        }
      })
    );
  }

  updateAffiliation(affiliation: UserAffiliation): Observable<User> {
    return this.http.post<User>(updateAffiliationUrl, affiliation, {
      params: {
        userId: affiliation.userId
      }
    });
  }

  getAffiliation(userId: number): Observable<UserAffiliation> {
    return this.http.get<UserAffiliation>(getAffiliationUrl, {
      params: {
        userId: userId
      }
    });
  }

  getUserAffiliationTypes(
    orgId: number,
    orgLevel: number,
    applyPermissionCheck = false
  ): Observable<UserAffiliationType[]> {
    return this.http.get<UserAffiliationType[]>(getUserAffiliationTypesUrl, {
      params: {
        orgId: orgId,
        orgLevel: orgLevel,
        applyPermissionCheck: applyPermissionCheck
      }
    });
  }

  getJobTitles(orgId: number, orgLevel: number): Observable<ReferenceTable[]> {
    return this.http.get<ReferenceTable[]>(getJobTitlesUrl, {
      params: {
        orgId: orgId,
        orgLevel: orgLevel
      }
    });
  }

  getRaceTitles(orgId: number, orgLevel: number): Observable<ReferenceTable[]> {
    return this.http.get<ReferenceTable[]>(getRaceTitlesUrl, {
      params: {
        orgId: orgId,
        orgLevel: orgLevel
      }
    });
  }

  getUserEmployers(orgId: number, orgLevel: number, referable = false): Observable<UserEmployer[]> {
    return this.http.get<UserEmployer[]>(getUserEmployersUrl, {
      params: {
        orgId: orgId,
        orgLevel: orgLevel,
        referable: referable
      }
    });
  }

  getMessageTags(): Observable<ReferenceTable[]> {
    return this.http.get<ReferenceTable[]>(getMessageTagsUrl);
  }

  getMessageSubjects(): Observable<ReferenceTable[]> {
    return this.http.get<ReferenceTable[]>(getMessageSubjectsUrl);
  }

  clearCache() {
    this.userCache.clear();
  }

  downloadActiveUsersReport() {
    return this.http.get('/active-users/excel.html', { responseType: 'blob' });
  }

  downloadUserAudit() {
    return this.http.get('/user-audit/excel.html', { responseType: 'blob' });
  }

  updateUserASMStatus(user: UserSearchRow): Observable<void> {
    return this.http.post<void>(updateUserASMStatusUrl, user).pipe(
      tap(() => {
        this._userASMStatusUpdate.next();
      })
    );
  }

  updateUserSudSmiProgramDashboardAccessStatus(user: UserSearchRow): Observable<void> {
    return this.http.post<void>(updateUserSudSmiProgramDashboardStatusUrl, user).pipe(
      tap(() => {
        this._userSudSmiAccessUpdate.next();
      })
    );
  }
}
